import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "AsociarJefes";

/* ================= Helpers ================= */
const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "—";

  const fecha = new Date(fechaStr);

  if (Number.isNaN(fecha.getTime())) return "—";

  return fecha.toLocaleDateString("es-CO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const ListarEnteVsJefe = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [asociaciones, setAsociaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  /* ================= CONFIG TOKEN ================= */
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  /* ================= PERMISOS MÓDULO ================= */
  const getModuloAsociarJefes = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || null
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return null;
    }
  };

  const moduloAsociarJefes = useMemo(() => getModuloAsociarJefes(), []);

  const puedeLeer =
    moduloAsociarJefes?.puedeLeer === true ||
    moduloAsociarJefes?.leer === true ||
    moduloAsociarJefes?.ver === true ||
    moduloAsociarJefes?.listar === true ||
    moduloAsociarJefes?.read === true ||
    moduloAsociarJefes?.view === true;

  const puedeCrear =
    moduloAsociarJefes?.puedeCrear === true ||
    moduloAsociarJefes?.crear === true ||
    moduloAsociarJefes?.create === true;

  const puedeActualizar =
    moduloAsociarJefes?.puedeActualizar === true ||
    moduloAsociarJefes?.actualizar === true ||
    moduloAsociarJefes?.editar === true ||
    moduloAsociarJefes?.update === true;

  const puedeEliminar =
    moduloAsociarJefes?.puedeEliminar === true ||
    moduloAsociarJefes?.eliminar === true ||
    moduloAsociarJefes?.delete === true;

  /* ================= VALIDAR SESIÓN ================= */
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  /* ================= Eliminar ================= */
  const deleteAsociacion = useCallback(
    async (asociacionId) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar asociaciones.",
          "warning"
        );
        return false;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡Esta acción no se puede deshacer!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return false;

        setDeletingId(asociacionId);

        const { data } = await axios.delete(
          `/api/asociarjefes/delete/${asociacionId}`,
          authConfig()
        );

        await Swal.fire({
          icon: "success",
          title: data?.message || "Asociación eliminada con éxito",
          showConfirmButton: false,
          timer: 1500,
        });

        setAsociaciones((prev) =>
          prev.filter((asociacion) => asociacion._id !== asociacionId)
        );

        return true;
      } catch (error) {
        console.error("Error al eliminar la asociación:", error);

        if (manejarErrorSesion(error)) return false;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
            "No se pudo eliminar la asociación.",
          "error"
        );

        return false;
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  /* ================= Obtener datos ================= */
  const getData = useCallback(async () => {
    if (!id) return;

    if (!puedeLeer) {
      setAsociaciones([]);
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/asociarjefes/listbyente/${id}`,
        authConfig()
      );

      const lista = Array.isArray(data?.asociaciones)
        ? data.asociaciones
        : Array.isArray(data?.asociar_asignar)
        ? data.asociar_asignar
        : Array.isArray(data)
        ? data
        : [];

      const formateadas = lista.map((asociacion) => {
        const jefe = asociacion.jefeObjectId || {};
        const ente = asociacion.enteObjectId || {};

        const nombreCompleto =
          [
            jefe.nombre1Jefe,
            jefe.nombre2Jefe,
            jefe.apellido1Jefe,
            jefe.apellido2Jefe,
          ]
            .filter(Boolean)
            .join(" ")
            .trim() || "Sin información";

        return {
          ...asociacion,
          nombreCompleto,
          documentoJefe: jefe.documentoJefe || "N/A",
          nombreEnteConformador: ente.nombreEnteconformador || "N/A",
          fechaAsignacion: formatearFecha(asociacion.fechaAsignacion),
        };
      });

      setAsociaciones(formateadas);
    } catch (error) {
      console.warn(
        "No hay asociaciones registradas para este ente o la respuesta vino vacía:",
        error?.response?.data?.message || error?.message
      );

      if (manejarErrorSesion(error)) return;

      setAsociaciones([]);
    } finally {
      setLoading(false);
    }
  }, [id, puedeLeer, manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData]);

  /* ================= Agregar JSX de funciones fuera de getData ================= */
  const dataWithFunciones = useMemo(() => {
    return (asociaciones || []).map((asociacion) => ({
      ...asociacion,

      funciones: (
        <div className="d-flex gap-2 flex-nowrap justify-content-end align-items-center">
          {puedeActualizar && (
            <button
              className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34 }}
              onClick={() =>
                navigate(`/enteconformador/asociar/editar/${asociacion._id}`)
              }
              title="Editar"
              type="button"
              disabled={loading || deletingId === asociacion._id}
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
          )}

          {puedeEliminar && (
            <button
              className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
              style={{ width: 34, height: 34 }}
              onClick={() => deleteAsociacion(asociacion._id)}
              title="Eliminar"
              type="button"
              disabled={loading || deletingId === asociacion._id}
            >
              {deletingId === asociacion._id ? (
                <i className="fa-solid fa-spinner fa-spin text-white"></i>
              ) : (
                <i className="fa-solid fa-trash"></i>
              )}
            </button>
          )}
        </div>
      ),
    }));
  }, [
    asociaciones,
    loading,
    deletingId,
    puedeActualizar,
    puedeEliminar,
    navigate,
    deleteAsociacion,
  ]);

  const columns = useMemo(
    () => [
      { Header: "Jefe", accessor: "nombreCompleto" },
      { Header: "Ente Conformador", accessor: "nombreEnteConformador" },
      { Header: "Fecha Asignación", accessor: "fechaAsignacion" },
      { Header: "Estado", accessor: "estadoAsignacion" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  if (!puedeLeer) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">
                Sin permiso de lectura
              </h5>

              <p className="mb-4">
                No tienes permiso para ver las asociaciones de jefes.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Asignaciones Ente vs Jefe
                </h5>

                <small className="text-secondary">
                  Gestión de asignaciones: crear, editar y eliminar.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() =>
                      navigate(`/enteconformador/asociar/nuevo/${id}`)
                    }
                    title="Nueva asociación"
                    disabled={loading}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando asociaciones…</div>
              </div>
            ) : Array.isArray(dataWithFunciones) &&
              dataWithFunciones.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataWithFunciones} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🔗</div>

                <div className="fw-semibold">
                  No hay asociaciones registradas
                </div>

                <div className="small">
                  Este ente aún no tiene jefes asociados.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-primary mt-3"
                    type="button"
                    onClick={() =>
                      navigate(`/enteconformador/asociar/nuevo/${id}`)
                    }
                    disabled={loading}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear primera asociación
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarEnteVsJefe;